import { motion } from "framer-motion";
import { MaskedLines } from "@/components/Reveal";
import { AXIOM } from "@/constants/testIds";
import { ArrowUpRight } from "lucide-react";

export default function Hero({ onContact }) {
  return (
    <section
      id="top"
      data-testid={AXIOM.hero.root}
      className="relative min-h-screen w-full overflow-hidden bg-bg flex items-center justify-center"
    >
      {/* Blueprint grid with radial mask */}
      <div className="absolute inset-0 blueprint-grid hero-mask pointer-events-none" />

      <div className="relative w-full max-w-[1200px] mx-auto px-6 md:px-12 py-32 md:py-40 text-center">
        <h1
          data-testid={AXIOM.hero.heading}
          className="font-display text-ink mt-6"
          style={{ fontSize: "clamp(42px, 7vw, 88px)", lineHeight: 1.02, letterSpacing: "-0.02em" }}
        >
          <MaskedLines
            lines={[
              "Complex engineering,",
              <>
                <span className="text-forge">solved</span>.
              </>,
            ]}
            delay={0.3}
          />
        </h1>

        <motion.p
          data-testid={AXIOM.hero.subheading}
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.9 }}
          className="mt-8 mx-auto text-ink/75 max-w-[62ch]"
          style={{ fontSize: "18px", lineHeight: 1.55 }}
        >
          AxiomForge is an end-to-end product development partner - design,
          simulation, prototyping, testing and manufacturing under one roof. We
          take the problems other teams walk away from and ship them.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.05 }}
          className="mt-10 flex flex-wrap items-center justify-center gap-3"
        >
          <button
            data-testid={AXIOM.hero.ctaPrimary}
            onClick={onContact}
            className="btn-forge group"
          >
            Start a project
            <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </button>
          <a href="#services" data-testid={AXIOM.hero.ctaSecondary} className="btn-ghost">
            See capabilities
          </a>
        </motion.div>

        {/* Blueprint diagram - subtle SVG hero motif */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1.4, delay: 1.25 }}
          className="mt-16 md:mt-20 mx-auto max-w-[820px]"
          aria-hidden="true"
        >
          <svg viewBox="0 0 820 90" className="w-full h-auto">
            <defs>
              <style>{`
                .draw { stroke-dasharray: 900; stroke-dashoffset: 900; animation: draw 1.4s ease .3s forwards; }
                @keyframes draw { to { stroke-dashoffset: 0; } }
              `}</style>
            </defs>
            <g fill="none" stroke="#12110f" strokeWidth="1.25">
              <path className="draw" d="M20 50 L200 50" />
              <path className="draw" d="M240 50 L420 50" />
              <path className="draw" d="M460 50 L640 50" />
              <path className="draw" d="M680 50 L800 50" />
              <circle cx="220" cy="50" r="4" fill="#ff5a1f" stroke="none" />
              <circle cx="440" cy="50" r="4" fill="#ff5a1f" stroke="none" />
              <circle cx="660" cy="50" r="4" fill="#ff5a1f" stroke="none" />
            </g>
            <g fontFamily="IBM Plex Mono, monospace" fontSize="10" fill="#6b6a68" letterSpacing="1.2">
              <text x="20" y="78">DESIGN</text>
              <text x="230" y="78">SIMULATE</text>
              <text x="450" y="78">PROTOTYPE</text>
              <text x="670" y="78">MANUFACTURE</text>
            </g>
          </svg>
        </motion.div>
      </div>
    </section>
  );
}
